// Shared field options for profile/onboarding fields.
// Single source of truth used by GrwmOnboarding and the guest progressive
// onboarding collector on the confidence card. Values are byte-identical to
// what GrwmOnboarding used inline historically — do not change a value or
// label without verifying the migration impact on existing profile rows.

export type Gender = "women" | "men" | "non-binary";

export type HeightValue =
  | "short"
  | "average_short"
  | "average_tall"
  | "tall";

export type FieldOption<V extends string = string> = {
  value: V;
  label: string;
};

export const HEIGHT_OPTIONS_BY_GENDER: Record<Gender, FieldOption<HeightValue>[]> = {
  women: [
    { value: "short", label: "Under 5'2" },
    { value: "average_short", label: "5'2 to 5'5" },
    { value: "average_tall", label: "5'5 to 5'8" },
    { value: "tall", label: "Over 5'8" },
  ],
  men: [
    { value: "short", label: "Under 5'7" },
    { value: "average_short", label: "5'7 to 5'10" },
    { value: "average_tall", label: "5'10 to 6'2" },
    { value: "tall", label: "Over 6'2" },
  ],
  "non-binary": [
    { value: "short", label: "Under 5'3" },
    { value: "average_short", label: "5'3 to 5'6" },
    { value: "average_tall", label: "5'6 to 5'9" },
    { value: "tall", label: "Over 5'9" },
  ],
};

// Body shape — women + non-binary. Men don't have a separate body_shape;
// they use BODY_BUILD_OPTIONS_MEN which doubles as shape + size.
export const BODY_SHAPE_OPTIONS_BY_GENDER: Record<
  Exclude<Gender, "men">,
  FieldOption[]
> = {
  women: [
    { value: "hourglass", label: "Hourglass" },
    { value: "pear", label: "Pear" },
    { value: "athletic", label: "Athletic" },
    { value: "curvy", label: "Curvy" },
    { value: "petite", label: "Petite" },
  ],
  "non-binary": [
    { value: "slim_lean", label: "Slim / lean" },
    { value: "athletic_broad", label: "Athletic / broad" },
    { value: "balanced_regular", label: "Balanced / regular" },
    { value: "curvy_fuller", label: "Curvy / fuller" },
    { value: "petite", label: "Petite" },
  ],
};

// Women-only build options (body_size).
export const BODY_SIZE_OPTIONS_WOMEN: FieldOption[] = [
  { value: "slim", label: "Slim" },
  { value: "average", label: "Average" },
  { value: "fuller", label: "Fuller" },
  { value: "plus_size", label: "Plus size" },
];

// Men-only build options — written to both bodyShape and bodySize.
export const BODY_BUILD_OPTIONS_MEN: FieldOption[] = [
  { value: "slim_lean", label: "Slim / lean" },
  { value: "regular_average", label: "Regular / average" },
  { value: "athletic_broad", label: "Athletic / broad" },
  { value: "stocky", label: "Stocky" },
  { value: "plus_size", label: "Plus size" },
];

export const TOP_SIZE_OPTIONS = ["XS", "S", "M", "L", "XL", "2XL", "3XL"] as const;

// -----------------------------------------------------------------------------
// Guest model profile — sessionStorage store for progressive onboarding on the
// confidence card. Mirrors `l2f_guest_size` pattern: plain JSON, no TTL. Picked
// up on signup and migrated into `profiles` (see src/routes/index.tsx).
// -----------------------------------------------------------------------------

export const GUEST_MODEL_STORAGE_KEY = "l2f_guest_model";

export type GuestModelProfile = {
  height: HeightValue | null;
  // value from BODY_SHAPE_OPTIONS_BY_GENDER (women/non-binary) or
  // BODY_BUILD_OPTIONS_MEN (men).
  body_shape: string | null;
  // value from BODY_SIZE_OPTIONS_WOMEN — women only; null otherwise.
  body_size: string | null;
};

const EMPTY_PROFILE: GuestModelProfile = {
  height: null,
  body_shape: null,
  body_size: null,
};

export function readGuestModelProfile(): GuestModelProfile | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(GUEST_MODEL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GuestModelProfile>;
    return {
      height: (parsed.height ?? null) as HeightValue | null,
      body_shape: parsed.body_shape ?? null,
      body_size: parsed.body_size ?? null,
    };
  } catch {
    return null;
  }
}

export function writeGuestModelProfile(profile: Partial<GuestModelProfile>): void {
  if (typeof window === "undefined") return;
  try {
    const current = readGuestModelProfile() ?? EMPTY_PROFILE;
    const next: GuestModelProfile = {
      height: profile.height !== undefined ? profile.height : current.height,
      body_shape:
        profile.body_shape !== undefined ? profile.body_shape : current.body_shape,
      body_size:
        profile.body_size !== undefined ? profile.body_size : current.body_size,
    };
    window.sessionStorage.setItem(GUEST_MODEL_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / serialise errors */
  }
}

export function clearGuestModelProfile(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(GUEST_MODEL_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
